import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import { mediaDims } from "@/lib/media-dims";
import { responsiveSources } from "@/lib/responsive-images";

type PassThrough = Omit<
  ComponentPropsWithoutRef<"img">,
  "src" | "alt" | "className" | "sizes" | "loading" | "width" | "height"
>;

interface MediaProps extends PassThrough {
  src: string;
  alt: string;
  className?: string;
  eager?: boolean;
  /** Layout hint for responsive selection; defaults to a card-width heuristic. */
  sizes?: string;
}

/**
 * Renders an <img> for still assets and a muted looping <video> for .mp4
 * assets (used in place of heavy animated GIFs). Still images are served
 * through <picture> with AVIF + WebP srcsets so the browser downloads the
 * smallest acceptable variant. Explicit intrinsic width/height are always set
 * to avoid layout shift. Videos only start downloading near the viewport.
 */
export const Media = forwardRef<HTMLElement, MediaProps>(
  ({ src, alt, className, eager, sizes, ...rest }, ref) => {
    const { width, height } = mediaDims(src);
    const isVideo = src.endsWith(".mp4");
    const videoRef = useRef<HTMLVideoElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const [inView, setInView] = useState(!!eager);

    useImperativeHandle(ref, () => (isVideo ? videoRef.current : imgRef.current) as HTMLElement);

    useEffect(() => {
      if (!isVideo || inView) return;
      const el = videoRef.current;
      if (!el) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            setInView(true);
            observer.disconnect();
          }
        },
        { rootMargin: "300px" },
      );
      observer.observe(el);
      return () => observer.disconnect();
    }, [isVideo, inView]);

    useEffect(() => {
      if (inView) videoRef.current?.play().catch(() => undefined);
    }, [inView]);

    if (isVideo) {
      return (
        <video
          {...(rest as unknown as ComponentPropsWithoutRef<"video">)}
          ref={videoRef}
          src={inView ? src : undefined}
          aria-label={alt}
          className={className}
          width={width}
          height={height}
          loop
          muted
          playsInline
          preload="none"
        />
      );
    }

    const { avif, webp } = responsiveSources(src);
    const sizesAttr =
      sizes ?? "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 720px";
    const hasVariants = Boolean(avif || webp);

    const img = (
      <img
        {...rest}
        ref={imgRef}
        src={src}
        alt={alt}
        className={className}
        width={width}
        height={height}
        sizes={hasVariants ? sizesAttr : undefined}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "auto"}
      />
    );

    if (!hasVariants) return img;

    return (
      <picture className="contents">
        {avif && <source type="image/avif" srcSet={avif} sizes={sizesAttr} />}
        {webp && <source type="image/webp" srcSet={webp} sizes={sizesAttr} />}
        {img}
      </picture>
    );
  },
);

Media.displayName = "Media";
